import React from 'react';
import { Icon, LocationMarkIcon, POIDetailsItem, POIDetailsItemProps } from '@placer-ui/components';
import { IPoi } from 'features/explore/model/poi-model/poi';
import { useExploreListItemName } from 'features/explore/components/explore-side-bar/components/explore-list/explore-list-item/hooks';
import { usePoiAttributes } from 'features/explore/model/poi-model/use-poi-attributes';
import { useIsFreemiumUser } from 'hooks/use-freemium-user/use-freemium-user';
import { numberFormat } from 'utils/number/number';
import {
    getVenueStatus,
    venueFlagInfoPublicPOIText,
} from 'ui-components/venue-status-indicator/v2/venue-status-indicator-v2';
import { SquareLogoProps } from 'ui-components/square-logo/square-logo';
import { BILLBOARD_ENTITY, MarketPOI, PlacePOI } from 'core/entities';
import { useSquareLogoProps } from 'features/explore/hooks/use-square-logo-props';
import { isAddressType } from 'utils/poi-provider/poi-provider';
import { ADDRESS_ENTITY, COORDINATES_ENTITY } from 'features/explore/constants';
import { ADDRESS_TITLE } from 'core/constants/address';
import { useExploreUIState } from 'features/explore/context/use-explore-ui-state';
import { formatFullAddress } from 'utils/address-formatter/address-formatter';
import { getCategoryIconUrlWithFallback } from 'core/entities/category/utils';
import { BasePlaceEntity } from '@placer-ui/types';
import { useSelectCategoriesData } from 'store/configuration/categories/selectors/selectors-hooks';

const SquareLogo = ({ name, logoUrl, placeType, icon }: SquareLogoProps) => {
    const imgSrc = logoUrl || icon;

    const imageStyles = {
        maxWidth: '100%',
        maxHeight: '100%',
    };

    if (placeType === 'address') {
        return <LocationMarkIcon />;
    } else if (imgSrc) {
        return <img style={imageStyles} crossOrigin="anonymous" src={imgSrc} alt={name} />;
    } else if (placeType === BILLBOARD_ENTITY) {
        return <Icon type={'TrafficPin'} weight={'duotone'} color={'gray'} size={24} />;
    }

    return null;
};

const getMetricLabel = (poi: IPoi): string | undefined => {
    const leadMetric = poi.getVisibleLeadMetric();

    if (!leadMetric) {
        return undefined;
    }

    const { value, displayName } = leadMetric;
    return value ? `${numberFormat(value, 1, true, true)} ${displayName}` : undefined;
};

const getInfoText = (poi: IPoi, isPrivacy: boolean): string | undefined => {
    const flag = poi.getFlag();
    const closingDate = poi.getClosingDate();

    if (poi.isRestricted()) {
        return 'Venue is part of a restricted area. Data is not available';
    } else if (isPrivacy) {
        return venueFlagInfoPublicPOIText[flag!];
    } else if (closingDate) {
        return getVenueStatus({
            flag,
            closingDate,
        });
    }

    return undefined;
};

export type ExplorePoiDetailsProps = Omit<POIDetailsItemProps, 'label' | 'address'> & {
    poi: IPoi;
    poiInfo?: BasePlaceEntity;
};

export const ExplorePoiDetails = ({ poi, poiInfo, ...rest }: ExplorePoiDetailsProps) => {
    const poiData = poiInfo ?? poi.getPoiData();
    const itemName = useExploreListItemName(poiData);
    const { shortAddress, categoriesBullets } = usePoiAttributes(poi);
    const logoProps = useSquareLogoProps(poi);
    const flag = poi.getFlag();
    const isFreemium = useIsFreemiumUser();
    const badge = poi.getBadgeValue();
    const hasLogo = !!poi.logoUrl.get();
    const isPrivacy = flag === 'privacy_concerns';

    const infoText = getInfoText(poi, isPrivacy);

    return (
        <POIDetailsItem
            label={itemName}
            address={shortAddress || ''}
            thumbnail={<SquareLogo {...logoProps} />}
            metricsValue={!flag ? getMetricLabel(poi) : undefined}
            bullets={categoriesBullets}
            badge={!isFreemium && badge ? `#${badge}` : undefined}
            tooltipIconProps={
                infoText
                    ? {
                          title: infoText,
                      }
                    : undefined
            }
            isCategory={!hasLogo}
            {...rest}
        />
    );
};

/**
 * @deprecated - TODO: remove with DeprecatedPOIDetails
 */
const deprecatedGetBullets = (poi: BasePlaceEntity, isMarket: boolean): string[] => {
    const group = PlacePOI.getGroupCategory(poi);
    const category = PlacePOI.getPrimaryCategory(poi);
    const baseCategoryLabel = MarketPOI.getBaseCategoryLabel(poi);

    const categoryBullets: string[] = [];

    if (group) {
        categoryBullets.push(group);
    }

    if (isMarket && baseCategoryLabel) {
        categoryBullets.push(baseCategoryLabel);
    }

    if (!isMarket && group !== category) {
        categoryBullets.push(category);
    }

    return categoryBullets;
};

/**
 * @deprecated - TODO: remove with SearchOptionsDialogComponent
 */
type DeprecatedPOIDetailsProps = {
    poi: BasePlaceEntity;
};

export const DeprecatedPOIDetails = ({ poi }: DeprecatedPOIDetailsProps) => {
    const { customPoiAddresses } = useExploreUIState();
    const categoriesIconData = useSelectCategoriesData();
    const isFreemium = useIsFreemiumUser();

    const itemName =
        isAddressType(poi) &&
        (!!customPoiAddresses[poi.provider_data?.entity_id || poi.id] ||
            poi.provider_data?.entity_id === ADDRESS_ENTITY ||
            (poi.category !== 'Address' && poi.provider_data?.entity_id !== COORDINATES_ENTITY))
            ? ADDRESS_TITLE
            : poi.name;
    const categoryIconUrl = getCategoryIconUrlWithFallback({
        categoriesIconData,
        info: poi,
    });
    const logoUrl = !poi.provider_data?.provider ? poi.logo_img : undefined;
    const icon = !poi.provider_data?.provider ? categoryIconUrl : undefined;
    const isMarket = MarketPOI.isMarketPOI(poi);
    const shortAddress = isMarket
        ? MarketPOI.getLocationLabel(poi)
        : formatFullAddress(poi.type, poi);
    const badge = isMarket ? poi.market_code : poi.store_id;

    return (
        <POIDetailsItem
            label={itemName}
            address={shortAddress}
            thumbnail={
                <SquareLogo
                    logoUrl={logoUrl}
                    name={poi.name}
                    placeType={isAddressType(poi) ? 'address' : poi.type}
                    icon={icon}
                />
            }
            bullets={deprecatedGetBullets(poi, isMarket)}
            badge={!isFreemium && badge ? `#${badge}` : undefined}
            isCategory={!logoUrl}
            isViewMode
        />
    );
};
